import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentChartBarIcon, Cog8ToothIcon } from '@heroicons/react/24/outline';
import { ROUTES } from '../../consts/general';
import ReportTemplate from '../../components/reports/ReportTemplate';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ReportEmailSettingsSidebar } from '../../components/reports/ReportEmailSettingsSidebar';
import { INBOUND_SHIPMENTS } from '../../consts/reports';
import { INBOUND_SHIPMENTS_TYPE } from '../../consts/cronTypes';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import {
  getReportEmailSettings,
  updateReportEmailSettings,
  sendReportPreviewEmail,
} from '../../slices/reportEmailSettings';
import { getReportSendHistory } from '../../slices/reportSendHistory';
import { updateCronInformation } from '../../slices/cronSlice';
import { buildCronInterval } from '../../helpers/cronExpression';
import { toast } from 'react-toastify';
import useDateRangeParams from '../../hooks/useDateRangeParams';

const DEFAULT_END_DATE = new Date().toISOString().split('T')[0];
const DEFAULT_START_DATE = (() => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().split('T')[0];
})();

const InboundShipmentsReport = ({
  marginFromSidebar
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { settings: allSettings, loading: isLoadingEmailSettings } = useSelector((s) => s.reportEmailSettings);
  const emailSettings = allSettings['inbound-shipments'] || {};
  const { sendHistory: allSendHistory, loading: isLoadingSendHistory } = useSelector((s) => s.reportSendHistory);
  const sendHistory = allSendHistory['inbound-shipments'] || [];

  const [reportData, setReportData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // totalRecords not used in UI
  const pageSize = 50;

  const { startDate, endDate, setDateRange } = useDateRangeParams(DEFAULT_START_DATE, DEFAULT_END_DATE);

  const [sortField, setSortField] = useState('po_date');
  const [sortDirection, setSortDirection] = useState('asc');

  const [totalPOs, setTotalPOs] = useState(0);
  const [uniquePOsHistory, setUniquePOsHistory] = useState({});
  const [isOpenSettings, setIsOpenSettings] = useState(false);
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);

  useEffect(() => {
    fetchReportMetadata();
    dispatch(getReportEmailSettings('inbound-shipments'));
    dispatch(getReportSendHistory('inbound-shipments'));
  }, [dispatch]);

  const fetchReportData = useCallback(async () => {
    if (!startDate || !endDate) return;
    const { signal, requestId } = beginRequest();
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        page: currentPage.toString(),
        pageSize: pageSize.toString()
      });
      if (sortField) {
        params.append('sortField', sortField);
        params.append('sortDirection', sortDirection);
      }
      const response = await fetch(`/api/v1/reports/${INBOUND_SHIPMENTS}?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        signal
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch report data');
      }
      const data = await response.json();
      if (!isLatest(requestId)) return;
      setReportData(data.data || []);
      // totalRecords currently unused in UI
      setTotalPages(data.totalPages || 1);
      setTotalPOs(data.totalPOs || 0);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('Error fetching report data:', err);
      if (!isLatest(requestId)) return;
      setError(err.message);
      setReportData([]);
    } finally {
      if (isLatest(requestId)) setIsLoading(false);
    }
  }, [startDate, endDate, currentPage, sortField, sortDirection, beginRequest, isLatest]);

  useEffect(() => {
    if (!initialReady) return;
    if (startDate && endDate) {
      fetchReportData();
    }
  }, [initialReady, fetchReportData, startDate, endDate]);

  useEffect(() => {
    setUniquePOsHistory(prev => ({
      ...prev,
      [currentPage]: uniquePOsOnPage.length
    }));
    // eslint-disable-next-line
  }, [reportData, currentPage]);

  const fetchReportMetadata = async () => {
    try {
      const response = await fetch(`/api/v1/reports/${INBOUND_SHIPMENTS}/metadata`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch report metadata');
      }
      const data = await response.json();
      setColumns((data.columns || []).map((column, index) => {
        if (index === 0) {
          return {
            ...column,
            sticky: true
          }
        }
        return column
      }));
    } catch (err) {
      console.error('Error fetching metadata:', err);
      setError('Failed to load report configuration');
    }
  };

  const handleDateChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
    setCurrentPage(1);
    setSortField(null);
    setSortDirection(null);
  };

  const handleSort = ({ field, direction }) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handleBackToReports = () => {
    navigate(ROUTES.REPORTS);
  };

  const handleExportCSV = async () => {
    if (!startDate || !endDate) {
      throw new Error('Please select a date range first');
    }
    const params = new URLSearchParams({ startDate, endDate });
    if (sortField) {
      params.append('sortField', sortField);
      params.append('sortDirection', sortDirection);
    }
    const response = await fetch(`/api/v1/reports/${INBOUND_SHIPMENTS}/export?${params}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Export failed');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const contentDisposition = response.headers.get('content-disposition');
    const filename = contentDisposition 
      ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
      : `inbound-shipments_${new Date().toISOString().slice(0, 10)}.csv`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    return { success: true };
  };

  const breadcrumbs = [
    { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
    { label: '' }
  ];

  const filtersComponent = (
    <DateRangePicker
      startDate={startDate}
      endDate={endDate}
      onDateChange={handleDateChange}
      label="Date Range"
      error={error && error.includes('date') ? error : null}
    />
  );

  const exportButton = (
    <CSVExportButton
      data={[]}
      columns={columns}
      filename={`${INBOUND_SHIPMENTS}`}
      onExport={handleExportCSV}
      disabled={!startDate || !endDate || isLoading}
      variant="outline"
    />
  );

  const getUniquePOs = (data) => {
    const set = new Set();
    data.forEach(row => set.add(row.po_number));
    return Array.from(set);
  };

  const uniquePOsOnPage = getUniquePOs(reportData);
  let startPO = 1;
  for (let i = 1; i < currentPage; i++) {
    startPO += uniquePOsHistory[i] || 0;
  }
  const endPO = Math.min(startPO + uniquePOsOnPage.length - 1, totalPOs);

  const paginationComponent = totalPages > 1 ? (
    <div className="flex items-center justify-between">
      <div className="text-sm text-gray-700">Showing {startPO}-{endPO} of {totalPOs} POs</div>
      <div className="flex items-center space-x-2">
        <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50">Previous</button>
        <span className="text-sm text-gray-700">Page {currentPage} of {totalPages}</span>
        <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50">Next</button>
      </div>
    </div>
  ) : null;

  const handleSuccessSubmit = async (formData) => {
    const payload = {
      emailRecipients: formData?.emailRecipients || [],
      scheduleType: formData?.schedule || null,
      dayOfMonth: formData?.dayOfMonth || null,
      slackChannelId: formData?.slackChannelId || null,
      dayOfWeek: formData?.dayOfWeek || null,
      timeOfDay: formData?.timeOfDay || null,
      emailMessage: formData?.emailMessage || null,
    };
    try {
      await dispatch(updateReportEmailSettings({ reportType: 'inbound-shipments', ...payload })).unwrap();
      const cronInterval = buildCronInterval({
        scheduleType: payload.scheduleType,
        dayOfMonth: payload.dayOfMonth,
        dayOfWeek: payload.dayOfWeek,
        timeOfDay: payload.timeOfDay,
      });
      if (!cronInterval) {
        toast.error('Invalid schedule settings (cron expression could not be built)', { position: 'bottom-right' });
        return;
      }
      const cronData = {
        id: emailSettings?.cronId,
        type: emailSettings?.cronType || INBOUND_SHIPMENTS_TYPE,
        cronInterval,
      };
      await dispatch(updateCronInformation({ singleData: cronData })).unwrap();
      toast.success('Settings saved successfully', { position: 'bottom-right' });
      setIsOpenSettings(false);
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to save settings', { position: 'bottom-right' });
    }
  };

  const sendPreviewEmail = async (formData) => {
    try {
      const payload = {
        email: formData?.emailSendPreviewTo,
        emailMessage: formData?.emailMessage || '',
        type: formData?.type || null,
        startDate: startDate,
        endDate: endDate,
        sortField: sortField || null,
        sortDirection: sortDirection || null,
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'inbound-shipments', ...payload })).unwrap();
      await dispatch(getReportSendHistory('inbound-shipments')).unwrap();
      toast.success('Preview email sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send preview', { position: 'bottom-right' });
    }
  };

  const sendPreviewSlack = async (formData) => {
    try {
      const payload = {
        slackChannelId: formData?.slackChannelId,
        emailMessage: formData?.emailMessage || '',
        type: formData?.type || null,
        startDate: startDate,
        endDate: endDate,
        sortField: sortField || null,
        sortDirection: sortDirection || null,
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'inbound-shipments', ...payload })).unwrap();
      await dispatch(getReportSendHistory('inbound-shipments')).unwrap();
      toast.success('Slack preview sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send slack preview', { position: 'bottom-right' });
    }
  };

  const renderTableRows = () => {
    const grouped = {};
    reportData.forEach(row => {
      if (!grouped[row.po_number]) grouped[row.po_number] = [];
      grouped[row.po_number].push(row);
    });
    const rows = [];
    Object.entries(grouped).forEach(([po, poRows]) => {
      poRows.sort((a, b) => new Date(a.box_shipdate || a.po_date) - new Date(b.box_shipdate || b.po_date));
      poRows.forEach((row, idx) => {
        rows.push(
          <tr key={`${po}-${row.box_shipdate || row.po_date}-${idx}`} className={idx !== 0 ? 'bg-gray-50' : 'bg-white'}>
            <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${idx !== 0 ? '!bg-gray-50' : 'bg-white'}`}>{po}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.po_date}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.box_shipdate}{row.missing_shipdate ? <span className="ml-2 inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800" title="Boxes were marked received but have no ship date recorded">Received w/o ship date</span> : null}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.box_shipping_method}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.estimated_receive_date}</td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.box_actual_date}</td>
          </tr>
        );
      });
    });
    return rows;
  };

  return (
    <>
      <ReportTemplate
        reportId={INBOUND_SHIPMENTS}
        description="Inbound shipment boxes grouped by PO, with ship and receive dates"
        breadcrumbs={breadcrumbs}
        onBack={handleBackToReports}
        marginFromSidebar={marginFromSidebar}
        isLoading={isLoading}
        error={error}
        filters={filtersComponent}
        exportButton={
          <div className="flex items-center space-x-2">
            {exportButton}
            <div>
              <Cog8ToothIcon
                className="h-5 w-5 text-primary cursor-pointer"
                onClick={() => {
                  setIsOpenSettings(true);
                }}
              />
            </div>
          </div>
        }
        data={reportData}
        cronType={emailSettings?.cronType || null}
        columns={columns}
        pagination={paginationComponent}
        emptyMessage="No inbound shipment data found for the selected date range."
        onSort={handleSort}
        sortField={sortField}
        sortDirection={sortDirection}
        enableSorting={true}
      >
        {renderTableRows()}
      </ReportTemplate>
      <ReportEmailSettingsSidebar
        reportType={'inbound-shipments'}
        isOpen={isOpenSettings}
        marginFromSidebar={marginFromSidebar}
        onClose={() => setIsOpenSettings(false)}
        isLoading={isLoadingEmailSettings}
        onSuccessSubmit={handleSuccessSubmit}
        sendPreviewEmail={sendPreviewEmail}
        sendPreviewSlack={sendPreviewSlack}
        defaultSettings={emailSettings || {}}
        sendHistory={sendHistory}
        isLoadingSendHistory={isLoadingSendHistory}
        downloadFilenamePrefix="inbound-shipments"
      />
    </>
  );
};

export default InboundShipmentsReport;

